import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import {
  getVolunteerHoursTargetConfig,
  saveVolunteerHoursTargetConfig,
  recalculateVolunteerHoursTargets,
} from 'api/volunteerHoursTargetApi';
import { getActiveSeason } from 'api/season';
import { getClubAgeGroups } from 'api/club/ageGroupApi';
import PinInputModal from 'components/admin/Pin/PinInputModal';
import { getClubSettings } from 'api/clubApi';

import styles from './VolunteerHoursTargetSettings.module.css';
import routes from 'routes';

import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PageHeader from 'components/NewDesign/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

import useGroupLabels from 'hooks/useGroupLabels';
import AgeBasedConfig from './components/AgeBasedConfig';
import FlatRateConfig from './components/FlatRateConfig';
import TieredConfig from './components/TieredConfig';

const VolunteerHoursTargetSettings = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  // Hours that follow the athlete read Age-Based and age group, or Year-Based and year group for a club that
  // groups by year level. The setting itself is stored the same way for both.
  const { words } = useGroupLabels();
  // A plain word, the same for as long as the club is: listing it below does not make the groups load again.
  const groupsWord = words.lower.groups;
  const navigate = useNavigate();

  // Targets can only be configured for the current active season.
  const [activeSeason, setActiveSeason] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Age groups for Age-Based strategy
  const [ageGroups, setAgeGroups] = useState([]);
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);

  // Strategy selection
  const [targetType, setTargetType] = useState('AgeGroupBased');

  // Configuration state for each strategy
  const [ageHoursConfig, setAgeHoursConfig] = useState({});
  const [flatRateHours, setFlatRateHours] = useState(20.0);
  const [tieredConfig, setTieredConfig] = useState([
    { minAthletes: 1, maxAthletes: 1, hours: 10.0 },
    { minAthletes: 2, maxAthletes: 2, hours: 15.0 },
    { minAthletes: 3, maxAthletes: null, hours: 20.0 },
  ]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [showPinInput, setShowPinInput] = useState(false);
  const [pendingConfigData, setPendingConfigData] = useState(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [adminPIN, setAdminPIN] = useState('');

  // Fetch the active season and club PIN on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!selectedClub?.clubID) {
        navigate(routes.admin.dashboard);
        return;
      }

      try {
        setInitialLoading(true);
        const [activeSeasonResponse, clubSettingsResponse] = await Promise.all([
          // No active season is a valid state, not a load failure.
          getActiveSeason(selectedClub.clubID).catch(() => null),
          getClubSettings(selectedClub.clubID),
        ]);

        if (activeSeasonResponse?.data) {
          setActiveSeason(activeSeasonResponse.data);
        }

        if (clubSettingsResponse.data) {
          setAdminPIN(clubSettingsResponse.data.adminPIN);
        }
      } catch (err) {
        console.log('[VolunteerHoursTargetSettings] Initial load error: ', err);
        toast.error('Failed to load club settings');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedClub?.clubID, navigate]);

  // Fetch age groups when needed (for Age-Based strategy)
  const fetchAgeGroups = useCallback(async () => {
    if (!selectedClub?.clubID) return;

    try {
      setLoadingAgeGroups(true);
      const response = await getClubAgeGroups(selectedClub.clubID);
      if (response.data) {
        setAgeGroups(response.data);
      }
    } catch (err) {
      console.log('[VolunteerHoursTargetSettings] Fetch age groups error: ', err);
      toast.error(`Failed to load ${groupsWord}`);
    } finally {
      setLoadingAgeGroups(false);
    }
  }, [selectedClub?.clubID, groupsWord]);

  useEffect(() => {
    if (activeSeason && targetType === 'AgeGroupBased') {
      fetchAgeGroups();
    }
  }, [activeSeason, targetType, fetchAgeGroups]);

  // Fetch the existing configuration for the active season
  useEffect(() => {
    const fetchConfig = async () => {
      if (!selectedClub?.clubID || !activeSeason?.seasonID) {
        setFetchingSettings(false);
        return;
      }

      try {
        setFetchingSettings(true);
        const response = await getVolunteerHoursTargetConfig(
          selectedClub.clubID,
          activeSeason.seasonID
        );

        if (response.data) {
          const { targetType: type, configurationJson } = response.data;
          setTargetType(type);

          const config = JSON.parse(configurationJson);

          switch (type) {
            case 'AgeGroupBased':
              setAgeHoursConfig(config.AgeHours || {});
              break;
            case 'FlatRate':
              setFlatRateHours(config.HoursPerMember || 20.0);
              break;
            case 'TieredByAthleteCount':
              setTieredConfig(
                config.Tiers || [
                  { minAthletes: 1, maxAthletes: 1, hours: 10.0 },
                  { minAthletes: 2, maxAthletes: 2, hours: 15.0 },
                  { minAthletes: 3, maxAthletes: null, hours: 20.0 },
                ]
              );
              break;
            default:
              break;
          }
        }
      } catch (err) {
        // No configuration exists yet - this is OK, use defaults
        console.log('[VolunteerHoursTargetSettings] No config found, using defaults');
      } finally {
        setFetchingSettings(false);
      }
    };

    fetchConfig();
  }, [selectedClub?.clubID, activeSeason?.seasonID]);

  const handleStrategyChange = (type) => {
    setTargetType(type);
  };

  const buildConfigJson = () => {
    switch (targetType) {
      case 'AgeGroupBased':
        return JSON.stringify({ AgeHours: ageHoursConfig });
      case 'FlatRate':
        return JSON.stringify({ HoursPerMember: flatRateHours });
      case 'TieredByAthleteCount':
        return JSON.stringify({ Tiers: tieredConfig });
      default:
        return '{}';
    }
  };

  const validateConfiguration = () => {
    switch (targetType) {
      case 'AgeGroupBased':
        const hasAnyHours = Object.values(ageHoursConfig).some(
          (hours) => hours > 0
        );
        if (!hasAnyHours) {
          toast.error(`Please set hours for at least one ${words.lower.group}`);
          return false;
        }
        break;
      case 'FlatRate':
        if (!flatRateHours || flatRateHours < 0) {
          toast.error('Please enter a valid hours per member value');
          return false;
        }
        break;
      case 'TieredByAthleteCount':
        for (let i = 0; i < tieredConfig.length; i++) {
          const tier = tieredConfig[i];
          if (!tier.minAthletes || tier.minAthletes < 1) {
            toast.error(`Tier ${i + 1}: Minimum athletes must be at least 1`);
            return false;
          }
          if (i < 2 && (!tier.maxAthletes || tier.maxAthletes < tier.minAthletes)) {
            toast.error(
              `Tier ${i + 1}: Maximum athletes must be greater than or equal to minimum`
            );
            return false;
          }
          if (tier.hours === undefined || tier.hours === '' || tier.hours < 0) {
            toast.error(`Tier ${i + 1}: Please enter valid hours`);
            return false;
          }
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleSave = async () => {
    if (!activeSeason) {
      toast.error('No active season to configure');
      return;
    }

    if (!validateConfiguration()) {
      return;
    }

    // Store the pending configuration and show PIN input
    setPendingConfigData({
      targetType,
      configurationJson: buildConfigJson(),
    });
    setShowPinInput(true);
  };

  const handleRecalculate = useCallback(async () => {
    if (!selectedClub?.clubID || !activeSeason) {
      toast.error('No active season to recalculate');
      return;
    }

    setIsRecalculating(true);

    toast.info('Recalculating volunteer hours targets. This may take a moment...', {
      autoClose: false,
      toastId: 'recalculating-volunteer-hours',
    });

    try {
      const response = await recalculateVolunteerHoursTargets(
        selectedClub.clubID,
        activeSeason.seasonID
      );

      toast.dismiss('recalculating-volunteer-hours');

      if (response.data) {
        const { totalRecalculated, successCount, errorCount } = response.data;
        if (errorCount > 0) {
          toast.warning(
            `Recalculation completed with ${errorCount} errors. ${successCount} of ${totalRecalculated} members updated.`,
            { autoClose: 5000 }
          );
        } else {
          toast.success(
            `Successfully recalculated targets for ${successCount} members.`,
            { autoClose: 5000 }
          );
        }
      }
    } catch (err) {
      toast.dismiss('recalculating-volunteer-hours');
      console.error('[VolunteerHoursTargetSettings] Recalculation error: ', err);
      toast.error('Failed to recalculate volunteer hours targets');
    } finally {
      setIsRecalculating(false);
    }
  }, [selectedClub?.clubID, activeSeason]);

  const handlePinVerification = async () => {
    if (!pendingConfigData || !selectedClub?.clubID || !activeSeason) return;

    setLoading(true);
    try {
      await saveVolunteerHoursTargetConfig(
        selectedClub.clubID,
        activeSeason.seasonID,
        pendingConfigData
      );
      toast.success('Volunteer hours target configuration saved');
      setShowPinInput(false);
      setPendingConfigData(null);
    } catch (err) {
      console.log('[VolunteerHoursTargetSettings] Save error: ', err);
      toast.error('Failed to save configuration');
      setLoading(false);
      return;
    }
    setLoading(false);

    // A saved configuration is only half the job — apply it to every member.
    await handleRecalculate();
  };

  const handleCancel = () => {
    navigate(routes.admin.dashboard);
  };

  const closePinInput = () => {
    setShowPinInput(false);
    setPendingConfigData(null);
  };

  if (initialLoading || fetchingSettings) {
    return (
      <AdminTablePageLayout loading={true}>
        <div>Loading...</div>
      </AdminTablePageLayout>
    );
  }

  return (
    <AdminTablePageLayout loading={false}>
      <PageHeader
        title="Volunteer Hours Target Settings"
        subTitle={activeSeason?.name || ''}
        primarySubTitle
      >
        <div style={{ display: 'flex', gap: '12px' }}>
          <CustomNewButton
            variant="primary2"
            onClick={handleRecalculate}
            label="Recalculate Targets"
            disabled={isRecalculating || loading || !activeSeason}
          />
          <CustomNewButton
            variant="outline"
            onClick={handleCancel}
            label="Cancel"
          />
        </div>
      </PageHeader>

      <div className={styles.formContainer}>
        {activeSeason ? (
          <>
            <div className={styles.activeSeasonBanner}>
              Configure volunteer hour requirements for club members based on
              their athletes. Targets apply to the current active season.
            </div>

            {/* Target Type Selection Card */}
            <div className={styles.formCard}>
              <h3 className={styles.cardTitle}>Volunteer Hours Calculation Method</h3>
              <p className={styles.cardSubtitle}>
                Select how volunteer hours are calculated for members
              </p>

              <div className={styles.strategyOptions}>
                {/* Flat Rate Option */}
                <label
                  className={`${styles.strategyOption} ${
                    targetType === 'FlatRate' ? styles.selected : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="targetType"
                    value="FlatRate"
                    checked={targetType === 'FlatRate'}
                    onChange={() => handleStrategyChange('FlatRate')}
                  />
                  <div className={styles.strategyContent}>
                    <div className={styles.strategyTitle}>Flat Rate</div>
                    <div className={styles.strategyDescription}>
                      Same hours for all members
                    </div>
                    <div className={styles.strategyBestFor}>
                      Best for: Simple, equal requirements
                    </div>
                  </div>
                </label>

                {/* Tiered Option */}
                <label
                  className={`${styles.strategyOption} ${
                    targetType === 'TieredByAthleteCount' ? styles.selected : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="targetType"
                    value="TieredByAthleteCount"
                    checked={targetType === 'TieredByAthleteCount'}
                    onChange={() => handleStrategyChange('TieredByAthleteCount')}
                  />
                  <div className={styles.strategyContent}>
                    <div className={styles.strategyTitle}>Tiered by Athlete Count</div>
                    <div className={styles.strategyDescription}>
                      Hours based on number of athletes
                    </div>
                    <div className={styles.strategyBestFor}>
                      Best for: Scaling requirements evenly by family size
                    </div>
                  </div>
                </label>

                {/* Age-Based Option */}
                <label
                  className={`${styles.strategyOption} ${
                    targetType === 'AgeGroupBased' ? styles.selected : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="targetType"
                    value="AgeGroupBased"
                    checked={targetType === 'AgeGroupBased'}
                    onChange={() => handleStrategyChange('AgeGroupBased')}
                  />
                  <div className={styles.strategyContent}>
                    <div className={styles.strategyTitle}>{words.value}-Based Hours</div>
                    <div className={styles.strategyDescription}>
                      Different hours per athlete {words.lower.group}
                    </div>
                    <div className={styles.strategyBestFor}>
                      Best for: Clubs with large variations in the number of athletes in each {words.lower.group}
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Configuration Card */}
            <div className={styles.formCard}>
              <h3 className={styles.cardTitle}>
                {targetType === 'AgeGroupBased' && `${words.value}-Based Hours Configuration`}
                {targetType === 'FlatRate' && 'Flat Rate Configuration'}
                {targetType === 'TieredByAthleteCount' &&
                  'Tiered by Athlete Count Configuration'}
              </h3>

              {loadingAgeGroups && targetType === 'AgeGroupBased' ? (
                <div>Loading {words.lower.groups}...</div>
              ) : (
                <>
                  {targetType === 'AgeGroupBased' && (
                    <AgeBasedConfig
                      ageGroups={ageGroups}
                      ageHoursConfig={ageHoursConfig}
                      onChange={setAgeHoursConfig}
                    />
                  )}
                  {targetType === 'FlatRate' && (
                    <FlatRateConfig
                      hoursPerMember={flatRateHours}
                      onChange={setFlatRateHours}
                    />
                  )}
                  {targetType === 'TieredByAthleteCount' && (
                    <TieredConfig tiers={tieredConfig} onChange={setTieredConfig} />
                  )}
                </>
              )}
            </div>

            <div className={styles.recalculationSection}>
              <div className={styles.recalculationText}>
                Saving changes will recalculate the volunteer hours targets for
                all club members.
              </div>
            </div>

            {/* Save Button - Centered on page */}
            <div className={styles.saveButtonRow}>
              <CustomNewButton
                variant="primary"
                onClick={handleSave}
                label={loading ? 'Saving...' : 'Save Configuration'}
                disabled={loading || isRecalculating || showPinInput}
              />
            </div>
          </>
        ) : (
          <div className={styles.formCard}>
            <h3 className={styles.cardTitle}>No Active Season</h3>
            <p className={styles.cardSubtitle}>
              Volunteer hours targets can only be configured for the current
              active season. Start a season first, then return to this page.
            </p>
          </div>
        )}
      </div>

      <PinInputModal
        clubId={selectedClub?.clubID}
        showPopup={showPinInput}
        closePopup={closePinInput}
        confirmPin={handlePinVerification}
        pinCode={adminPIN}
        description={`Changing volunteer hours targets is restricted to admin users with PIN code access. Enter the admin PIN to save the configuration for ${activeSeason?.name || 'the active season'} and recalculate the targets for all club members.`}
      />
    </AdminTablePageLayout>
  );
};

export default VolunteerHoursTargetSettings;
